import types from '../../actions/types'

import { combineReducers } from 'redux'
import { isNil, get } from 'lodash'

import uuid from '../../helpers/uuid'

const defaultPageSize = 50

const keyedReducer = (key, defaultState, reducer) => (state = defaultState, action) => {
    if (action.key != null && action.key === key) return reducer(state, action)
    return state
}

// NOTE: mode is now controlled at the component level and is NOT switchable

export const createPivotTableReducer = ({
                                            key,
                                            defaultSortColumn,
                                            defaultSortDirection = 'asc',
                                            pageSize = defaultPageSize,
                                            defaultGridStretch = false,
                                            defaultAreAllRowsSelected = false,
                                            defaultCanSelectRows = false,
                                            retainEdits = true,
                                            onNewEntity = e => e,
                                            canSearch = true,
                                        }) => {
    return combineReducers({
        search: keyedReducer(key, '', (state, action) => {
            switch (action.type) {
                case types.PIVOT_TABLE_SEARCH:
                    return canSearch
                        ? action.search
                        : state
                case types.PIVOT_TABLE_UPDATE_PARAMS:
                  return get(action, 'params.search', state)
                case types.PIVOT_TABLE_RESET:
                    return ''
                default:
                    return state
            }
        }),
        filters: keyedReducer(key, [], (state, action) => {
            switch (action.type) {
                case types.PIVOT_TABLE_UPDATE_FILTERS:
                    return action.filters
                case types.PIVOT_TABLE_RESET:
                    return []
                default:
                    return state
            }
        }),
        page: keyedReducer(key, 0, (state, action) => {
            switch (action.type) {
                case types.PIVOT_TABLE_PAGE:
                    return Math.max(action.page, 0)
                case types.PIVOT_TABLE_SEARCH:
                case types.PIVOT_TABLE_RESET:
                    return 0
                case types.PIVOT_TABLE_RESET_PAGE:
                    return 0
                case types.PIVOT_TABLE_PAGE_SIZE_UPDATE:
                    return isNil(action.page) ? state : action.page
                default:
                    return state
            }
        }),
        pageSize: keyedReducer(key, pageSize, (state, action) => {
            switch (action.type) {
                case types.PIVOT_TABLE_PAGE_SIZE_UPDATE:
                    return action.pageSize
                default:
                    return state
            }
        }),
        sortColumn: keyedReducer(key, defaultSortColumn, (state, action) => {
            switch (action.type) {
                case types.PIVOT_TABLE_SORT:
                    return action.sortColumn
                case types.PIVOT_TABLE_UPDATE_PARAMS:
                  return get(action, 'params.sortColumn', state)
                default:
                    return state
            }
        }),
        sortDirection: keyedReducer(key, defaultSortDirection, (state, action) => {
            switch (action.type) {
                case types.PIVOT_TABLE_SORT:
                    return action.sortDirection
                case types.PIVOT_TABLE_UPDATE_PARAMS:
                  return get(action, 'params.sortDirection', state)
                default:
                    return state
            }
        }),
        areAllRowsSelected: keyedReducer(key, defaultAreAllRowsSelected, (state, action) => {
            switch (action.type) {
                case types.PIVOT_TABLE_SELECT_ALL_ROWS:
                    return true
                case types.PIVOT_TABLE_DESELECT_ALL_ROWS:
                    return false
                case types.PIVOT_TABLE_RESET:
                    return false
                case types.PIVOT_TABLE_TOGGLE_SELECTED_ENTITY:
                    if (action.entity.selected) {
                        return false
                    } else {
                        return state
                    }
                default:
                    return state
            }
        }),
        canSelectRows: keyedReducer(key, defaultCanSelectRows, (state, action) => {
            switch (action.type) {
                case types.PIVOT_TABLE_CAN_SELECT_ROWS:
                    return true
                case types.PIVOT_TABLE_CANNOT_SELECT_ROWS:
                    return false
                default:
                    return state
            }
        }),
        columns: keyedReducer(key, [], (state, action) => {
            switch (action.type) {
                case types.PIVOT_TABLE_SET_VISIBLE_COLUMNS:
                    return action.visibleColumns
              case types.PIVOT_TABLE_UPDATE_PARAMS:
                return get(action, 'params.columns', state)
                default:
                    return state
            }
        }),
        columnSizes: keyedReducer(key, {}, (state, action) => {
            switch (action.type) {
                case types.PIVOT_TABLE_SET_COLUMN_SIZES:
                    return action.columnSizes
                default:
                    return state
            }
        }),
        groups: keyedReducer(key, null, (state, action) => {
            switch (action.type) {
                case types.PIVOT_TABLE_SET_GROUPS:
                    return action.groups
                case types.PIVOT_TABLE_UPDATE_PARAMS:
                  return get(action, 'params.groups', state)
                default:
                    return state
            }
        }),
        gridStretch: keyedReducer(key, defaultGridStretch, (state, action) => {
            switch (action.type) {
                case types.PIVOT_TABLE_SET_GRID_STRETCH:
                    return action.gridStretch
                default:
                    return state
            }
        }),
        edited: keyedReducer(key, {}, (state, action) => {
            switch (action.type) {
                case types.PIVOT_TABLE_EDIT_DATA: {
                    if (action.save || retainEdits === false) return state
                    const ret = {...state}
                    ret[action.entityKey] = action.entity
                    return ret
                }
                case types.PIVOT_TABLE_EDIT_DATA_MULTI: {
                    if (action.save || retainEdits === false) return state
                    const ret = {...state}
                    action.entities.forEach(entity => {
                        ret[entity.id] = entity
                    })
                    return ret
                }
                case types.PIVOT_TABLE_LOAD_SUCCESS:
                    if (action.revertEdits) return {}
                    return state
                case types.PIVOT_TABLE_SAVE_SUCCESS:
                    return {}
                default:
                    return state
            }
        }),
        exporting: keyedReducer(key, false, (state, action) => {
            switch (action.type) {
                case types.PIVOT_TABLE_EXPORT_START:
                    return true
                case types.PIVOT_TABLE_EXPORT_FINISH:
                    return false
                default:
                    return state
            }
        }),
        exportingRows: keyedReducer(key, null, (state, action) => {
            switch (action.type) {
                case types.PIVOT_TABLE_EXPORT_PROGRESS:
                    return action.rowsTotal
                case types.PIVOT_TABLE_EXPORT_FINISH:
                    return null
                default:
                    return state
            }
        }),
        exportedRows: keyedReducer(key, null, (state, action) => {
            switch (action.type) {
                case types.PIVOT_TABLE_EXPORT_PROGRESS:
                    return action.rowsDone
                case types.PIVOT_TABLE_EXPORT_FINISH:
                    return null
                default:
                    return state
            }
        }),
        data: combineReducers({
            fetching: keyedReducer(key, false, (state, action) => {
                switch (action.type) {
                    case types.PIVOT_TABLE_SET_FETCHING:
                        return action.fetching
                    case types.PIVOT_TABLE_LOAD_ATTEMPT:
                        return true
                    case types.PIVOT_TABLE_LOAD_SUCCESS:
                    case types.PIVOT_TABLE_LOAD_FAILURE:
                        return false
                    default:
                        return state
                }
            }),
            error: keyedReducer(key, null, (state, action) => {
                switch (action.type) {
                    case types.PIVOT_TABLE_LOAD_SUCCESS:
                    case types.PIVOT_TABLE_LOAD_ATTEMPT:
                        return null
                    case types.PIVOT_TABLE_LOAD_FAILURE:
                        return action.message || 'Oops, a problem occurred'
                    default:
                        return state
                }
            }),
            errorCode: keyedReducer(key, null, (state, action) => {
                switch (action.type) {
                    case types.PIVOT_TABLE_LOAD_SUCCESS:
                    case types.PIVOT_TABLE_LOAD_ATTEMPT:
                        return null
                    case types.PIVOT_TABLE_LOAD_FAILURE:
                        return action.code || 0
                    default:
                        return state
                }
            }),
            count: keyedReducer(key, null, (state, action) => {
                switch (action.type) {
                    case types.PIVOT_TABLE_LOAD_SUCCESS:
                        return action.count
                    case types.PIVOT_TABLE_LOAD_FAILURE:
                        return 0
                    case types.PIVOT_TABLE_RESET:
                        return null
                    default:
                        return state
                }
            }),
            loaded: keyedReducer(key, false, (state, action) => {
                switch (action.type) {
                    case types.PIVOT_TABLE_LOAD_ATTEMPT:
                        return true
                    case types.PIVOT_TABLE_RESET:
                        return false
                    case types.PIVOT_TABLE_RESET_LOADED:
                        return false
                    default:
                        return state
                }
            }),
            data: keyedReducer(key, [], (state, action) => {
                switch (action.type) {
                    case types.PIVOT_TABLE_LOAD_FAILURE:
                        return null
                    case types.PIVOT_TABLE_LOAD_SUCCESS:
                        return action.data
                    case types.PIVOT_TABLE_RESET:
                        return []

                    case types.PIVOT_TABLE_EDIT_DATA:
                        const {entity} = action
                        return state.map(e => e.id === entity.id ? entity : e)

                    case types.PIVOT_TABLE_EDIT_DATA_MULTI:
                        const {entities} = action
                        return state.map(entity => entities.find(e => e.id === entity.id) || entity)

                    case types.PIVOT_TABLE_SAVE_SUCCESS: {
                        // Reset edited stats
                        return state.map(e => ({...e, _editedKeys: null}))
                    }

                    case types.PIVOT_TABLE_SET_ENTITIES:
                        return action.entities

                    case types.PIVOT_TABLE_ADD_ENTITY:
                        return [
                            ...state,
                            onNewEntity({
                                id: uuid(),
                                ...action.entity,
                                isNew: true,
                            }, state),
                        ]
                    case types.PIVOT_TABLE_ADD_ENTITY_AT_INDEX:
                        return state.splice(action.index, 0, action.entity)
                    case types.PIVOT_TABLE_EDIT_ENTITY:
                        if (!isNil(action.index)) {
                            return state.map((entity, index) => {
                                if (index !== action.index) return entity
                                return {
                                    ...entity,
                                    ...action.entity,
                                }
                            })
                        }
                        return state.map((entity) => {
                            if (entity.id !== action.entity.id) return entity
                            return {
                                ...entity,
                                ...action.entity,
                            }
                        })

                    case types.PIVOT_TABLE_COPY_ENTITY:
                        return [
                            ...state,
                            onNewEntity({
                                id: uuid(),
                                ...action.entity,
                                guid: uuid(),
                                isNew: true,
                            }, state),
                        ]

                    case types.PIVOT_TABLE_DELETE_ENTITY:
                        action.entity['deleted'] = true
                        return [
                            ...state.slice(0, action.index),
                            action.entity,
                            ...state.slice(action.index + 1),
                        ]

                    case types.PIVOT_TABLE_REORDER_ENTITY:
                        const ret = [
                            ...state.slice(0, action.fromIndex),
                            ...state.slice(action.fromIndex + 1),
                        ]
                        ret.splice(action.toIndex, 0, state[action.fromIndex])
                        return ret

                    case types.PIVOT_TABLE_TOGGLE_SELECTED_ENTITY:
                        if (action.entity['selected']) {
                            action.entity['selected'] = !action.entity['selected']
                        } else {
                            action.entity['selected'] = true
                        }
                        return [
                            ...state.slice(0, action.index),
                            action.entity,
                            ...state.slice(action.index + 1),
                        ]
                    case types.PIVOT_TABLE_SELECT_ALL_ROWS:
                        return [
                            ...state.slice().map((entity) => {
                                entity['selected'] = true
                                return entity
                            }),
                        ]
                    case types.PIVOT_TABLE_DESELECT_ALL_ROWS:
                        return [
                            ...state.slice().map((entity) => {
                                entity['selected'] = false
                                return entity
                            }),
                        ]
                    case types.PIVOT_TABLE_CHANGE_SELECTED_ROWS:
                        return [
                            ...state.slice().map((entity) => {
                                entity['selected'] = action.selectedIds.includes(entity.id)
                                return entity
                            }),
                        ]
                    case types.PIVOT_TABLE_UPDATE_DATA:
                        return action.data || []
                    default:
                        return state
                }
            }),
        }),
        visible: keyedReducer(key, false, (state, action) => {
            switch (action.type) {
                case types.PIVOT_TABLE_SET_VISIBILITY:
                    return action.visible
                default:
                    return state
            }
        }),
    })
}
